import { useInsforge } from '../provider/InsforgeProvider';
import { useEffect, useMemo } from 'react';
import type { ReactNode } from 'react';
import type { InsforgeUser } from '../types';
import { resolveAuthPath } from '../lib/path-utils';

export interface ProtectProps {
  children: ReactNode;
  fallback?: ReactNode;
  redirectTo?: string;
  condition?: (user: InsforgeUser) => boolean;
  onRedirect?: (url: string) => void;
}

/**
 * Protected route component that redirects unauthenticated users.
 *
 * @component
 * @example
 * ```tsx
 * // Basic usage
 * <Protect redirectTo="/sign-in">
 *   <Dashboard />
 * </Protect>
 *
 * // With custom redirect handler (e.g., for Next.js router)
 * <Protect
 *   redirectTo="/sign-in"
 *   onRedirect={(url) => router.push(url)}
 * >
 *   <AdminPanel />
 * </Protect>
 *
 * // With custom condition (role-based access)
 * <Protect
 *   redirectTo="/unauthorized"
 *   condition={(user) => user.role === 'admin'}
 * >
 *   <AdminContent />
 * </Protect>
 * ```
 *
 * @param {ReactNode} children - Content to protect
 * @param {ReactNode} [fallback] - Fallback UI while loading
 * @param {string} [redirectTo='/sign-in'] - Redirect URL
 * @param {function} [condition] - Custom access condition
 * @param {function} [onRedirect] - Custom redirect handler (default: window.location)
 */
export function Protect({
  children,
  fallback,
  redirectTo = '/sign-in',
  condition,
  onRedirect,
}: ProtectProps): ReactNode {
  const { userId, user } = useInsforge();

  // Resolve redirect path with proper base path handling
  const resolvedRedirectTo = useMemo(() => resolveAuthPath(redirectTo), [redirectTo]);

  useEffect(() => {
    // undefined = not loaded yet, don't redirect
    // null = loaded and signed out, redirect
    // string = signed in, check condition
    if (userId === null) {
      // Not signed in, redirect
      if (onRedirect) {
        onRedirect(resolvedRedirectTo);
      } else {
        window.location.href = resolvedRedirectTo;
      }
    } else if (userId && condition && user) {
      // Signed in but condition failed, redirect
      if (!condition(user)) {
        if (onRedirect) {
          onRedirect(resolvedRedirectTo);
        } else {
          window.location.href = resolvedRedirectTo;
        }
      }
    }
  }, [userId, resolvedRedirectTo, condition, user, onRedirect]);

  // Show fallback while loading (undefined = not loaded)
  if (userId === undefined) {
    return fallback || <div className="insforge-loading">Loading...</div>;
  }

  // Don't render if not signed in (null = signed out)
  if (userId === null) {
    return fallback || null;
  }

  // Don't render if condition fails
  if (condition && user && !condition(user)) {
    return fallback || null;
  }

  return <>{children}</>;
}
